import React, { useState, useEffect } from 'react';
import {
    Edit,
    SimpleForm,
    TextInput,
    SelectInput,
    BooleanInput,
    required,
    useTranslate,
    Toolbar,
    SaveButton,
    DeleteButton,
    useRedirect,
    useDataProvider,
    useNotify,
    useRefresh,
    useRecordContext,
    regex,
    minLength,
    DateField,
    TextField,
} from 'react-admin';
import {
    Box,
    Typography,
    Alert,
    Card,
    CardContent,
    IconButton,
    Chip,
    Autocomplete,
    TextField as MuiTextField,
    Checkbox,
    Divider,
} from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { CreditCard, Person, LocationCity, Settings, ArrowBack, Info, History } from '@mui/icons-material';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';

const RFID_CARD_KNOWN_FIELDS = new Set([
    'cardNumber', 'cardType', 'appUserId', 'rfidUserId', 'vendorId', 'siteIds',
    'validFrom', 'validUntil', 'maxUses', 'isActive', 'isBlocked', 'blockedReason',
]);

const mapRfidCardBackendPath = (path: string): string => {
    const stripped = path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');
    if (stripped === 'appUserId.rfidUserId') {
        return 'rfidUserId';
    }
    return stripped;
};

const rfidCardFieldLabelKey = (field: string): string =>
    RFID_CARD_KNOWN_FIELDS.has(field)
        ? `resources.rfid-cards.fields.${field}`
        : field;

const EditToolbar = () => {
    const record = useRecordContext();
    const canDelete = !!record && !record.lastUsedAt && (record.currentUses ?? 0) === 0;

    return (
        <Toolbar
            sx={{
                backgroundColor: '#FAFAFA',
                borderTop: '1px solid #E0E0E0',
                px: { xs: 2, sm: 3 },
                py: 2,
                mt: { xs: 2, sm: 4 },
                display: 'flex',
                justifyContent: 'space-between',
                '& .MuiButton-root': {
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                },
            }}
        >
            <SaveButton
                variant="contained"
                sx={{
                    backgroundColor: '#00C853',
                    '&:hover': {
                        backgroundColor: '#00A843',
                        boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
                    },
                }}
            />
            {canDelete && (
                <DeleteButton
                    mutationMode="pessimistic"
                    sx={{
                        color: '#D32F2F',
                        '&:hover': {
                            backgroundColor: 'rgba(211, 47, 47, 0.04)',
                        },
                    }}
                />
            )}
        </Toolbar>
    );
};

const PageHeader = () => {
    const redirect = useRedirect();
    const translate = useTranslate();

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: { xs: 2, sm: 4 },
        }}>
            <IconButton
                onClick={() => redirect('/rfid-cards')}
                size="large"
                sx={{
                    flexShrink: 0,
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:hover': {
                        backgroundColor: '#F5F5F5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    },
                }}
                title={translate('ra.action.back')}
            >
                <ArrowBack />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '2.125rem' }, mb: 0.5, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                    {translate('resources.rfid-cards.edit.title')}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {translate('resources.rfid-cards.edit.subtitle')}
                </Typography>
            </Box>
        </Box>
    );
};

const SiteSelection = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const { setValue } = useFormContext();
    const [sites, setSites] = useState<any[]>([]);
    const [selectedSites, setSelectedSites] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (record?.vendorId) {
            loadVendorSites();
        }
    }, [record?.vendorId]);

    const loadVendorSites = async () => {
        try {
            setLoading(true);
            // Get vendor details to find allowed sites
            const { data: vendor } = await dataProvider.getOne('vendors', { id: record!.vendorId });

            if (vendor.allowedSites && vendor.allowedSites.length > 0) {
                // Get site details for allowed sites
                const { data: allSites } = await dataProvider.getList('sites', {
                    pagination: { page: 1, perPage: 1000 },
                    sort: { field: 'name', order: 'ASC' },
                    filter: { ids: vendor.allowedSites },
                });
                setSites(allSites);

                // Initialize selected sites from record
                if (record?.sites || record?.allowedSites || record?.siteIds) {
                    const currentSiteIds = record.sites?.map((s: any) => s.siteId) ||
                                          record.allowedSites ||
                                          record.siteIds || [];
                    const selectedSiteObjects = allSites.filter((site: any) =>
                        currentSiteIds.includes(site.id)
                    );
                    setSelectedSites(selectedSiteObjects);
                    setValue('siteIds', currentSiteIds);
                }
            } else {
                setSites([]);
            }
        } catch (error) {
            notify('Error loading vendor sites', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleSiteChange = (event: any, value: any[]) => {
        setSelectedSites(value);
        const siteIds = value.map(site => site.id);
        setValue('siteIds', siteIds.length > 0 ? siteIds : [], { shouldDirty: true, shouldValidate: true });
    };

    return (
        <Box>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                {translate('resources.rfid-cards.edit.siteSelectionHelp') ||
                 'Select which sites this card can access. If no sites are selected, the card will have access to all vendor sites.'}
            </Typography>

            {sites.length === 0 && !loading && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    {translate('resources.rfid-cards.edit.noSitesAvailable') || 'No sites are assigned to the selected vendor'}
                </Alert>
            )}

            <Autocomplete
                multiple
                options={sites}
                value={selectedSites}
                onChange={handleSiteChange}
                loading={loading}
                getOptionLabel={(option) => option.name || ''}
                renderOption={(props, option, { selected }) => (
                    <Box component="li" {...props}>
                        <Checkbox
                            checked={selected}
                            sx={{ mr: 1 }}
                        />
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body1">
                                {option.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {[option.addressLine1, option.address, option.city].filter(Boolean).join(', ')}
                            </Typography>
                        </Box>
                    </Box>
                )}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                        <Chip
                            label={option.name}
                            size="small"
                            {...getTagProps({ index })}
                            sx={{
                                backgroundColor: '#E8F5E9',
                                color: '#1B5E20',
                                '& .MuiChip-deleteIcon': {
                                    color: '#388E3C',
                                    '&:hover': {
                                        color: '#1B5E20',
                                    },
                                },
                            }}
                        />
                    ))
                }
                renderInput={(params) => (
                    <MuiTextField
                        {...params}
                        label={translate('resources.rfid-cards.fields.assignedSites') || 'Assigned Sites'}
                        placeholder={loading ? 'Loading sites...' : translate('resources.rfid-cards.edit.searchSites') || 'Search and select sites...'}
                        helperText={`${selectedSites.length} ${translate('resources.rfid-cards.siteCount') || 'sites'} selected`}
                    />
                )}
                sx={{
                    '& .MuiAutocomplete-inputRoot': {
                        flexWrap: 'wrap',
                        gap: 0.5,
                        padding: '8px 9px',
                    },
                    '& .MuiAutocomplete-input': {
                        minWidth: { xs: '100px !important', sm: '200px !important' },
                    },
                }}
                disableCloseOnSelect
                fullWidth
            />
        </Box>
    );
};

const FormContent = () => {
    const translate = useTranslate();
    const record = useRecordContext();
    const { register } = useFormContext();

    const validateCardNumber = [
        required(translate('ra.validation.required')),
        minLength(4, translate('ra.validation.minLength', { min: 4 })),
        regex(/^[A-F0-9]+$/, translate('resources.rfid-cards.validation.cardNumberFormat') ||
              'Must contain only hexadecimal characters (0-9, A-F)'),
    ];

    if (!record) {
        return null;
    }

    return (
        <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', pb: { xs: 10, sm: 2 }, '& .MuiFormControl-root': { width: '100%' } }}>
            <PageHeader />

            <Alert severity="info" sx={{ mb: { xs: 2, sm: 3 } }}>
                <Typography variant="body2">
                    {translate('resources.rfid-cards.edit.infoMessage') ||
                     'Manage RFID card details and access permissions. Changes take effect immediately.'}
                </Typography>
            </Alert>

            {/* Card Information Section */}
            <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <CreditCard sx={{ mr: 2, color: '#00C853' }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                            {translate('resources.rfid-cards.sections.cardInfo') || 'Card Information'}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'grid', gap: 2 }}>
                        <TextInput
                            source="id"
                            disabled
                            fullWidth
                            label={translate('resources.rfid-cards.fields.id')}
                        />

                        <TextInput
                            source="cardNumber"
                            label={translate('resources.rfid-cards.fields.cardNumber')}
                            validate={validateCardNumber}
                            fullWidth
                            disabled
                            helperText={translate('resources.rfid-cards.edit.cardNumberHelperText') ||
                                      'Card number cannot be changed after creation'}
                            sx={{ '& input': { fontFamily: 'monospace', textTransform: 'uppercase' } }}
                        />

                        <SelectInput
                            source="cardType"
                            label={translate('resources.rfid-cards.fields.cardType')}
                            choices={[
                                { id: 'STANDARD', name: translate('resources.rfid-cards.types.standard') || 'Standard' },
                                { id: 'ADMIN', name: translate('resources.rfid-cards.types.admin') || 'Admin' },
                                { id: 'GUEST', name: translate('resources.rfid-cards.types.guest') || 'Guest' },
                                { id: 'FLEET', name: translate('resources.rfid-cards.types.fleet') || 'Fleet' },
                            ]}
                            fullWidth
                            helperText={translate('resources.rfid-cards.edit.cardTypeHelperText') ||
                                      'Select the type of access this card provides'}
                        />
                    </Box>
                </CardContent>
            </Card>

            {/* Assignment Section (Read-only) */}
            <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <Person sx={{ mr: 2, color: '#00C853' }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                            {translate('resources.rfid-cards.sections.assignment') || 'Assignment'}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'grid', gap: 2 }}>
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {translate('resources.rfid-cards.fields.vendor')}
                            </Typography>
                            <Typography variant="body1">
                                {record.vendorName || 'N/A'}
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {translate('resources.rfid-cards.fields.user')}
                            </Typography>
                            <Typography variant="body1">
                                {record.userName || 'N/A'}
                            </Typography>
                            {record.userEmail && (
                                <Typography variant="caption" color="text.secondary">
                                    {record.userEmail}
                                </Typography>
                            )}
                        </Box>

                        <Alert severity="info" icon={<Info />}>
                            <Typography variant="caption">
                                {translate('resources.rfid-cards.edit.assignmentHelperText') ||
                                 'Vendor and user assignments cannot be changed after creation. Create a new card if you need to reassign.'}
                            </Typography>
                        </Alert>
                    </Box>
                </CardContent>
            </Card>

            {/* Site Access Section */}
            <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <LocationCity sx={{ mr: 2, color: '#00C853' }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                            {translate('resources.rfid-cards.sections.siteAccess') || 'Site Access'}
                        </Typography>
                    </Box>

                    <SiteSelection />
                    <input type="hidden" {...register('siteIds')} />
                </CardContent>
            </Card>

            {/* Settings Section */}
            <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <Settings sx={{ mr: 2, color: '#00C853' }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                            {translate('resources.rfid-cards.sections.settings') || 'Settings'}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'grid', gap: 2 }}>
                        <BooleanInput
                            source="isActive"
                            label={translate('resources.rfid-cards.fields.isActive')}
                            sx={{
                                '& .MuiFormControlLabel-label': {
                                    fontSize: '1rem',
                                },
                            }}
                        />

                        {!record.isActive && (
                            <Alert severity="warning">
                                <Typography variant="body2">
                                    {translate('resources.rfid-cards.edit.deactivateWarning') ||
                                     'Deactivating this card will immediately prevent it from being used at charging stations.'}
                                </Typography>
                            </Alert>
                        )}
                    </Box>
                </CardContent>
            </Card>

            {/* Usage History Section */}
            {record.recentSessions && record.recentSessions.length > 0 && (
                <Card sx={{ overflow: 'hidden' }}>
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                            <History sx={{ mr: 2, color: '#00C853' }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                {translate('resources.rfid-cards.sections.usageHistory') || 'Recent Usage'}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', gap: { xs: 2, sm: 4 }, mb: 3 }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    {translate('resources.rfid-cards.fields.usageCount')}
                                </Typography>
                                <Typography variant="h6">
                                    {record.currentUses || 0}
                                </Typography>
                            </Box>
                            {record.lastUsedAt && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        {translate('resources.rfid-cards.fields.lastUsed')}
                                    </Typography>
                                    <DateField source="lastUsedAt" record={record} showTime />
                                </Box>
                            )}
                        </Box>

                        {record.recentSessions.slice(0, 5).map((session: any, index: number) => (
                            <React.Fragment key={session.id}>
                                {index > 0 && <Divider sx={{ my: 1 }} />}
                                <Box sx={{ py: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box>
                                            <Typography variant="body2">
                                                {session.siteName} - {session.chargerName}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                <DateField source="startTime" record={session} showTime />
                                            </Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'right' }}>
                                            <Typography variant="body2">
                                                {session.energyDelivered} kWh
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                ${session.totalCost}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </React.Fragment>
                        ))}
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export const RfidCardsEdit = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const refresh = useRefresh();
    const [serverErrors, setServerErrors] = React.useState<Record<string, string>>({});

    return (
        <Edit
            title={false}
            actions={false}
            component="div"
            mutationMode="pessimistic"
            mutationOptions={{
                onSuccess: () => {
                    setServerErrors({});
                    notify('ra.notification.updated', {
                        type: 'success',
                        messageArgs: { smart_count: 1 },
                    });
                    refresh();
                },
                onError: (error: any) => {
                    const apiError = error?.body || error;
                    const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
                    const fieldErrors: Record<string, string> = {};
                    const summaryLines: string[] = [];
                    if (rawErrors) {
                        for (const [path, msgs] of Object.entries(rawErrors)) {
                            const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
                            const translated = translateServerMessage(rawMsg, translate);
                            const field = mapRfidCardBackendPath(path);
                            fieldErrors[field] = translated;
                            const label = translate(rfidCardFieldLabelKey(field), { _: field });
                            summaryLines.push(`• ${label}: ${translated}`);
                        }
                    }
                    setServerErrors(fieldErrors);

                    const headerKey = apiError?.errorCode
                        ? `errors.${apiError.errorCode}`
                        : 'validation.formHasErrors';
                    const header = translate(headerKey, { _: apiError?.message || 'Error' });

                    if (summaryLines.length > 0) {
                        notify(`${header}\n${summaryLines.join('\n')}`, {
                            type: 'error',
                            multiLine: true,
                            autoHideDuration: 10000,
                        });
                    } else {
                        notify(header, { type: 'error', autoHideDuration: 6000 });
                    }
                },
            }}
            sx={{
                maxWidth: '100%',
                overflow: 'hidden',
                backgroundColor: 'transparent',
                '& .RaEdit-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
            transform={data => {
                // Transform the form data to match backend API expectations.
                // We do NOT silently default siteIds to [] when undefined —
                // an undefined siteIds field means "user did not touch the
                // site selection" and the backend should leave assignments
                // alone (the update handler treats `siteIds === undefined`
                // as a no-op for site assignments). Forcing [] would silently
                // wipe the card's site grants.
                const formData = { ...data };

                // Remove fields that shouldn't be updated
                delete formData.id;
                delete formData.cardNumber;
                delete formData.vendorId;
                delete formData.rfidUserId;
                delete formData.appUserId;
                delete formData.vendorName;
                delete formData.userName;
                delete formData.userEmail;
                delete formData.sites;
                delete formData.allowedSites;
                delete formData.recentSessions;
                delete formData.currentUses;
                delete formData.lastUsedAt;
                delete formData.createdAt;
                delete formData.updatedAt;
                delete formData.createdBy;

                return formData;
            }}
        >
            <SimpleForm
                toolbar={<EditToolbar />}
                sx={{
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                    '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                        px: { xs: 0, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    },
                    padding: { xs: 0, sm: 'inherit' },
                }}
            >
                <ServerErrorBinder errors={serverErrors} />
                <FormContent />
            </SimpleForm>
        </Edit>
    );
};